import {
  PutRequest,
  useLazySamplesWaterSampleListQuery,
  useSamplesWaterSampleDeleteMutation,
  useSamplesWaterSampleListQuery,
  useSamplesWaterSamplePutMutation,
  useSamplesWaterSampleShowQuery,
} from "@/services/backend/samples/api/water-sample-api";
import { DeleteRequest } from "@/services/backend/samples/api/air-microbial-api";
import { WaterSample } from "@/services/backend/samples/types/water-sample";
import { UUID } from "@bitory-gmbh/nidavellir";
import {
  documentDbGetAll,
  documentDbGetAllDeleted,
  documentDbPut,
} from "@/shared/lib/storage/indexdb/document-db";
import { GSMDocumentKind } from "@/services/backend/documents/types/kind";
import t from "@/lang/lang";
import {
  Mutation,
  useDocumentDBDelete,
  useDocumentDBGet,
  useDocumentDBGetAll,
  useDocumentDBPut,
} from "@/shared/lib/storage/indexdb/document-db-hooks";
import { useParams } from "react-router";
import { FilterMode } from "@/shared/lib/filter/filter";
import { useStaleContext } from "./stale-context";
import { OfflineState, useOfflineContext } from "./offline-context";

/*
 * useWaterSampleOffline prepares two functions. One to
 * prepare the water samples for offline usage and one to
 * sync the offline state back to the server.
 *
 * When calling the functions, the offline database must
 * be set up. The caller must catch thrown errors.
 */
export function useWaterSampleOffline(workOrderId: UUID) {
  const { setSynced } = useOfflineContext();
  const { addStale } = useStaleContext();
  const [load] = useLazySamplesWaterSampleListQuery();
  const [put] = useSamplesWaterSamplePutMutation();
  const [doDelete] = useSamplesWaterSampleDeleteMutation();

  const prepMsg = t("Speichere Wasserproben ...");
  const syncMsg = t("Synchronisiere Wasserproben ...");
  const doneMsg = t("Wasserproben");

  const prepareWaterSamples = async () => {
    // Load the latest state
    const result = await load({
      document: { active: true, values: [workOrderId] },
    });
    if (result.error) {
      throw result.error;
    }

    // When going offline, data is set to stale, such that
    // all data is reloaded from the offline database.
    addStale("waterSample");

    // Append to synced state for user information
    setSynced((prev) => [...prev, prepMsg]);

    // Save to index DB
    await Promise.all(
      result.data!.samples.map(async (sample) => {
        await documentDbPut<WaterSample>(
          GSMDocumentKind.WorkOrder,
          workOrderId,
          "waterSample",
          sample,
        );
      }),
    );

    // Replace in synced state for user information
    setSynced((prev) => prev.map((p) => (p === prepMsg ? doneMsg : p)));
  };

  const syncWaterSamples = async () => {
    // Append to synced state for user information
    setSynced((prev) => [...prev, syncMsg]);

    // Load from index DB
    const idbSamples = await documentDbGetAll<WaterSample>(
      GSMDocumentKind.WorkOrder,
      workOrderId,
      "waterSample",
    );
    const deletedIdbSamples = await documentDbGetAllDeleted<WaterSample>(
      GSMDocumentKind.WorkOrder,
      workOrderId,
      "waterSample",
    );

    // Sync to server
    // We first PUT all non-deleted or new entities back to the server
    await Promise.all(
      idbSamples.map(async (sample) => {
        await put(sample).unwrap();
      }),
    );
    // Then we delete the entities that where deleted offline.
    await Promise.all(
      deletedIdbSamples.map(async (sample) => {
        await doDelete({ id: sample.id }).unwrap();
      }),
    );

    // Replace in synced state for user information
    setSynced((prev) => prev.map((p) => (p === syncMsg ? doneMsg : p)));
  };

  return { prepareWaterSamples, syncWaterSamples };
}

export function usePutWaterSample(): Mutation<WaterSample, WaterSample> {
  const { workOrderId } = useParams();
  const { offlineState } = useOfflineContext();

  const [putWaterSampleMutation, { isLoading, error, isSuccess, reset }] =
    useSamplesWaterSamplePutMutation();

  const [
    putWaterSampleIndexedDB,
    {
      isLoading: idbIsLoading,
      error: idbError,
      isSuccess: idbIsSuccess,
      reset: idbReset,
    },
  ] = useDocumentDBPut<PutRequest, WaterSample>(
    GSMDocumentKind.WorkOrder,
    workOrderId!,
    "waterSample",
  );

  switch (offlineState) {
    case OfflineState.ONLINE:
      return [putWaterSampleMutation, { isLoading, error, isSuccess, reset }];
    case OfflineState.OFFLINE:
      return [
        putWaterSampleIndexedDB,
        {
          isLoading: idbIsLoading,
          error: idbError,
          isSuccess: idbIsSuccess,
          reset: idbReset,
        },
      ];
    default:
      return [
        () => {
          throw new Error(
            `Put of sample set not possible in state ${offlineState}.`,
          );
        },
        {
          isLoading: false,
          error: undefined,
          isSuccess: false,
          reset: () => null,
        },
      ];
  }
}

export function useGetWaterSample(id: string) {
  const { workOrderId } = useParams();
  const { offlineState } = useOfflineContext();

  const {
    data: serverData,
    isLoading,
    error,
  } = useSamplesWaterSampleShowQuery({
    id,
  });

  const {
    data: idbData,
    isLoading: idbIsLoading,
    error: idbError,
  } = useDocumentDBGet<WaterSample>(
    GSMDocumentKind.WorkOrder,
    workOrderId!,
    "waterSample",
    id,
  );

  switch (offlineState) {
    case OfflineState.ONLINE:
      return {
        data: serverData,
        isLoading,
        error,
      };
    case OfflineState.OFFLINE:
      return {
        data: idbData,
        isLoading: idbIsLoading,
        error: idbError,
      };
    default:
      return {
        data: undefined,
        isLoading: false,
        error: undefined,
      };
  }
}

export function useListWaterSamples() {
  const { workOrderId } = useParams();
  const { offlineState } = useOfflineContext();

  const {
    data: serverData,
    isLoading,
    error,
  } = useSamplesWaterSampleListQuery({
    document: {
      active: true,
      mode: FilterMode.Or,
      values: [workOrderId!],
    },
  });

  const {
    data: idbData,
    isLoading: idbIsLoading,
    error: idbError,
  } = useDocumentDBGetAll<WaterSample>(
    GSMDocumentKind.WorkOrder,
    workOrderId!,
    "waterSample",
  );

  switch (offlineState) {
    case OfflineState.ONLINE:
      return {
        data: serverData?.samples,
        isLoading,
        error,
      };
    case OfflineState.OFFLINE:
      return {
        data: idbData,
        isLoading: idbIsLoading,
        error: idbError,
      };
    default:
      return {
        data: undefined,
        isLoading: false,
        error: undefined,
      };
  }
}

export function useDeleteWaterSample(): Mutation<DeleteRequest, null> {
  const { workOrderId } = useParams();
  const { offlineState } = useOfflineContext();

  const [deleteWaterSampleMutation, { isLoading, error, isSuccess, reset }] =
    useSamplesWaterSampleDeleteMutation();

  const [
    deleteWaterSampleIndexedDB,
    {
      isLoading: idbIsLoading,
      error: idbError,
      isSuccess: idbIsSuccess,
      reset: idbReset,
    },
  ] = useDocumentDBDelete(
    GSMDocumentKind.WorkOrder,
    workOrderId!,
    "waterSample",
  );
  const deleteIdb = (request: DeleteRequest) =>
    deleteWaterSampleIndexedDB(request.id);

  switch (offlineState) {
    case OfflineState.ONLINE:
      return [
        deleteWaterSampleMutation,
        { isLoading, error, isSuccess, reset },
      ];
    case OfflineState.OFFLINE:
      return [
        deleteIdb,
        {
          isLoading: idbIsLoading,
          error: idbError,
          isSuccess: idbIsSuccess,
          reset: idbReset,
        },
      ];
    default:
      return [
        () => {
          throw new Error(
            `Delete of sample not possible in state ${offlineState}.`,
          );
        },
        {
          isLoading: false,
          error: undefined,
          isSuccess: false,
          reset: () => null,
        },
      ];
  }
}
